import _ from 'underscore';
import { Component } from 'react';
import propTypes from 'prop-types';
import AssetPickerTypes from '~/components/common/asset-picker/asset-picker-types';
import { APP_CONSTANTS } from '~/common/explicit-exports';
import { WrTooltip } from '../../../styled-components/common/wr-tooltip';
import AssetPickerGuide from './asset-picker-guide';
import AssetPickerFolder from './asset-picker-folder';
import AssetPickerAsset from './asset-picker-asset';
import AssetPickerCreateAssetDropdown from './create-assets/asset-picker-create-asset-dropdown';

class AssetPickerFolderChildren extends Component {
  componentDidMount() {
    if (this.props.isEditable) this.makeSortable();
  }

  render() {
    let createButton;
    if (this.props.isEditable)
      createButton = (
        <AssetPickerCreateAssetDropdown
          itemFolderId={this.props.folder.id}
          updateCallback={this.props.updateCallback}
        />
      );

    const children = this.getChildComponents();
    if (!createButton && children.length === 0) return <span className="asset-picker-asset--children--no-children" />;

    return (
      <div className="asset-picker-asset--children">
        <ul ref={(children) => (this.children = children)} className="js-asset-picker-sortable">
          {children}
          {/* NOTE: The line below is a hack to allow dropping onto empty lists. You can set a min-height instead,
                    however it doesn't work too well unless a large value is used. */}
          {this.props.isEditable && children.length == 0 ? (
            <div className="asset-picker-asset--children-empty-message">
              <WrTooltip
                arrow={false}
                content="This folder is empty"
                direction="down-start"
                tagName="span"
                zIndex={2000}
                inline
              >
                This folder is empty
              </WrTooltip>
            </div>
          ) : null}
        </ul>
        {createButton}
      </div>
    );
  }

  makeSortable = () => {
    const me = this;

    $(this.children).sortable({
      forcePlaceholderSize: true,
      placeholder: 'course-box-section-placeholder',
      handle: '.allow-draggable',
      connectWith: '.js-asset-picker-sortable',
      containment: '.admin-guide-viewer-pane',
      distance: '5',
      scrollSpeed: 5,
      scrollSensitivity: 100,
      stop(event, ui) {
        me.props.itemMoveCallback(event, ui, this);
      },
    });
  };

  getChildComponents = () => {
    const { viewableAssets, isActiveById } = this.props;
    let allChildren = [];

    if (viewableAssets.includes(AssetPickerTypes.FOLDER)) {
      const folders = this.getFolders(isActiveById);
      if (folders) allChildren = allChildren.concat(folders);
    }

    if (viewableAssets.includes(AssetPickerTypes.GUIDE)) {
      const guides = this.getGuides(isActiveById);
      if (guides) allChildren = allChildren.concat(guides);
    }

    if (viewableAssets.includes(AssetPickerTypes.WORKFLOW)) {
      const workflows = this.getItems(this.props.folder.trainingWorkflows, isActiveById);
      if (workflows) allChildren = allChildren.concat(workflows);
    }

    if (viewableAssets.includes(AssetPickerTypes.SERIES)) {
      const series = this.getItems(this.props.folder.trainingSeries, isActiveById);
      if (series) allChildren = allChildren.concat(series);
    }

    if (viewableAssets.includes(AssetPickerTypes.UNIVERSITY)) {
      const universities = this.getItems(this.props.folder.universities, isActiveById);
      if (universities) allChildren = allChildren.concat(universities);
    }

    if (viewableAssets.includes(AssetPickerTypes.CHALLENGE)) {
      const challenges = this.getItems(this.props.folder.challenges, isActiveById);
      if (challenges) allChildren = allChildren.concat(challenges);
    }

    if (viewableAssets.includes(AssetPickerTypes.RESOURCE)) {
      const resources = this.getItems(this.props.folder.resources, isActiveById);
      if (resources) allChildren = allChildren.concat(resources);
    }

    if (viewableAssets.includes(AssetPickerTypes.SCORM)) {
      const scormContents = this.getItems(
        _.filter(this.props.folder.topLevelContents, (tlc) => tlc.type === AssetPickerTypes.SCORM),
        isActiveById,
      );
      if (scormContents) allChildren = allChildren.concat(scormContents);
    }

    if (viewableAssets.includes(AssetPickerTypes.EVENT_TEMPLATE)) {
      const eventTemplateContents = this.getItems(
        _.filter(this.props.folder.topLevelContents, (tlc) => tlc.type === AssetPickerTypes.EVENT_TEMPLATE),
        isActiveById,
      );
      if (eventTemplateContents) allChildren = allChildren.concat(eventTemplateContents);
    }

    if (viewableAssets.includes(AssetPickerTypes.CLASS)) {
      const trainingClasses = this.getItems(this.props.folder.trainingClasses, isActiveById);
      if (trainingClasses) allChildren = allChildren.concat(trainingClasses);
    }

    if (viewableAssets.includes(AssetPickerTypes.ACADEMY_CERTIFICATION)) {
      const academyCertifications = this.getItems(this.props.folder.academyCertifications, isActiveById);
      if (academyCertifications) allChildren = allChildren.concat(academyCertifications);
    }

    if (viewableAssets.includes(AssetPickerTypes.ACADEMY_PATH)) {
      const academyPaths = this.getItems(this.props.folder.academyPaths, isActiveById);
      if (academyPaths) allChildren = allChildren.concat(academyPaths);
    }

    if (viewableAssets.includes(AssetPickerTypes.ACADEMY_COLLECTION)) {
      const academyCollections = this.getItems(this.props.folder.academyCollections, isActiveById);
      if (academyCollections) allChildren = allChildren.concat(academyCollections);
    }

    if (viewableAssets.includes(AssetPickerTypes.ACADEMY_COLLECTION_CONTENT)) {
      const contents = this.props.folder.academyCollectionContents?.map(
        ({
          contentType,
          content: { displayTitle, name },
          academyCollection: { name: collectionName, academyCustomPage },
          ...rest
        }) =>
          ({
            ...rest,
            type: AssetPickerTypes.ACADEMY_COLLECTION_CONTENT,
            contentSource: contentType,
            name: displayTitle || name,
            displayTitle: displayTitle || name,
            subtext: academyCustomPage ? `Page: ${academyCustomPage.title}` : `Collection: ${collectionName}`,
          }) || [],
      );

      const academyCollectionContents = this.getItems(contents, isActiveById);
      if (academyCollectionContents) allChildren = allChildren.concat(academyCollectionContents);
    }

    if (viewableAssets.includes(AssetPickerTypes.ACADEMY_EVENT)) {
      const academyEvents = this.getItems(this.props.folder.academyEvents, isActiveById);
      if (academyEvents) allChildren = allChildren.concat(academyEvents);
    }

    if (viewableAssets.includes(AssetPickerTypes.LINKEDIN_LEARNING)) {
      const isLinkedInLearningCourse = (tlc) =>
        tlc.type === APP_CONSTANTS.CONTENT_PROVIDER_TOP_LEVEL_CONTENT_TYPE &&
        tlc.contentSource === AssetPickerTypes.LINKEDIN_LEARNING;

      const linkedinLearningCourses = this.getItems(
        _.filter(this.props.folder.topLevelContents, isLinkedInLearningCourse),
        isActiveById,
      );
      if (linkedinLearningCourses) allChildren = allChildren.concat(linkedinLearningCourses);
    }

    if (viewableAssets.includes(AssetPickerTypes.UDEMY)) {
      const isUdemyCourse = (tlc) =>
        tlc.type === APP_CONSTANTS.CONTENT_PROVIDER_TOP_LEVEL_CONTENT_TYPE &&
        tlc.contentSource === AssetPickerTypes.UDEMY;

      const udemyCourses = this.getItems(_.filter(this.props.folder.topLevelContents, isUdemyCourse), isActiveById);
      if (udemyCourses) allChildren = allChildren.concat(udemyCourses);
    }

    const orderedChildren = _.sortBy(allChildren, (asset) => asset.order);

    const orderedChildComponents = _.map(orderedChildren, (asset) => asset.component);

    return orderedChildComponents;
  };

  getFolders = (isActiveById) => {
    const me = this;

    return _.map(this.props.folder.subfolders, (subFolder) => {
      const parentEditors = me.props.parentEditors.concat(me.props.folder.users);

      return {
        order: subFolder.order,
        component: (
          <AssetPickerFolder
            {...me.props}
            key={subFolder.id}
            folder={subFolder}
            isActive={isActiveById[subFolder.id]}
            parentEditors={parentEditors}
            assetsAreLinks={this.props.assetsAreLinks}
            currentItemTimestamp={this.props.currentItemTimestamp}
          />
        ),
      };
    });
  };

  getGuides = (isActiveById) => {
    const me = this;

    return _.map(this.props.folder.courses, (guide) => ({
      order: guide.order,
      component: (
        <AssetPickerGuide
          {...me.props}
          key={guide.id}
          guide={guide}
          isActive={isActiveById[guide.id]}
          isActiveById={isActiveById}
          assetsAreLinks={this.props.assetsAreLinks}
          currentItemTimestamp={this.props.currentItemTimestamp}
        />
      ),
    }));
  };

  getItems = (items, isActiveById) => {
    const me = this;

    const filteredItems = (items || []).filter((item) => {
      if (this.props.excludedAssetIds) {
        return !this.props.excludedAssetIds.includes(item.id);
      }

      return true;
    });

    return _.map(filteredItems, (item) => ({
      order: item.order,
      component: (
        <AssetPickerAsset
          {...me.props}
          key={item.id}
          asset={item}
          isActive={isActiveById[item.id]}
          assetsAreLinks={this.props.assetsAreLinks}
          currentItemTimestamp={this.props.currentItemTimestamp}
        />
      ),
    }));
  };
}

AssetPickerFolderChildren.propTypes = {
  isEditable: propTypes.bool,
  isActiveById: propTypes.objectOf(propTypes.bool),
  assetsAreLinks: propTypes.bool,
  updateCallback: propTypes.func.isRequired,
  itemMoveCallback: propTypes.func.isRequired,
  viewableAssets: propTypes.arrayOf(propTypes.string).isRequired,
  parentEditors: propTypes.array.isRequired,
  folder: propTypes.shape({
    id: propTypes.string.isRequired,
    trainingWorkflows: propTypes.array,
    trainingSeries: propTypes.array,
    universities: propTypes.array,
    challenges: propTypes.array,
    courses: propTypes.array,
    resources: propTypes.array,
    trainingClasses: propTypes.array,
    subfolders: propTypes.array,
    users: propTypes.array,
  }).isRequired,
  excludedAssetIds: propTypes.arrayOf(propTypes.string),
};

export default AssetPickerFolderChildren;
